import {Component, EventEmitter, Input, Output, ChangeDetectionStrategy} from '@angular/core';
import {NgClass} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {IconDefinition} from '@fortawesome/fontawesome-svg-core';
import {faBolt, faCircle, faCircleCheck, faPen, faXmark} from '@fortawesome/free-solid-svg-icons';
import {createMemo} from '../../../../memo';
import {BindingCandidate, BindingSource, JsonSchema, WidgetName} from '../schema-form.types';
import {enumLabel, isTypeCompatible, structuralWidget} from '../widget-registry';
import {SfLeafComponent} from './sf-leaf.component';
import {classifyStatusValue} from './status-classify';

/** Ein Eintrag der Werte-Legende unter einer gebundenen Quelle. */
export interface BindingLegendEntry {
  value: string;
  label: string;
  /** Nur bei Status-Feldern: dasselbe Icon wie in der Status-Auswahl. */
  icon?: IconDefinition;
  colorClass?: string;
}

/**
 * Die Bindung EINES Werts: Festwert eintragen oder einen Wert aus einem vorherigen Schritt wählen.
 *
 * Ersetzt das Ziehen einer Datenkante. Gespeichert wird in beiden Fällen dasselbe — ein Festwert
 * direkt, eine Auswahl als `${nodeId.outputs.key}` — die Komponente entscheidet also nur über die
 * Bedienung, nicht über das Format.
 *
 * **Rein präsentational und ohne Workflow-Wissen**: Kandidaten und Quellanzeige kommen als
 * einfache Datenstrukturen herein ({@link BindingCandidate}/{@link BindingSource}). Dadurch kann
 * dasselbe Widget an zwei Stellen laufen: für Eingangs-Ports über
 * `app-workflow-value-binding` (dort löst das Panel die Quelle gegen das Manifest auf) und für
 * Einstellungen über `x-widget: 'value-binding'` im Manifest — z.B. Wert A/Wert B je Fall des
 * Bedingungs-Moduls.
 */
@Component({
  selector: 'sf-value-binding',
  standalone: true,
  imports: [FormsModule, FaIconComponent, NgClass, SfLeafComponent],
  host: {'class': 'block w-full'},
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="flex flex-col gap-1 relative">
      @if (isReference) {
        <!-- Gebunden: die Quelle steht read-only da, mit ihren möglichen Werten. -->
        <!-- Gebunden: EINE Zeile wie ein normales Auswahlfeld. Die möglichen Werte stehen nicht im
             Feld (dort liefen sie bei schmalem Panel unter Stift und X), sondern als Legende darunter. -->
        <div class="flex items-center gap-2 h-[38px] px-3 rounded-lg border"
             [class]="resolvedSource?.dangling
                        ? 'bg-red-50 border-red-200'
                        : 'bg-gray-50 border-gray-300'">
          @if (resolvedSource?.dangling) {
            <fa-icon [icon]="faXmark" class="text-red shrink-0"></fa-icon>
            <span class="text-sm text-red-700 flex-1 min-w-0 truncate" [title]="value">
              Quelle nicht mehr verfügbar<span class="text-red-400"> ({{ value }})</span>
            </span>
          } @else {
            <fa-icon [icon]="faCircleCheck" class="text-green shrink-0"></fa-icon>
            <span class="text-sm text-gray-900 flex-1 min-w-0 truncate"
                  [title]="$safeNavigationMigration(resolvedSource?.nodeName) + ' · ' + $safeNavigationMigration(resolvedSource?.name)">
              {{ resolvedSource?.nodeName }} · <span class="font-medium">{{ resolvedSource?.name }}</span>
            </span>
          }
          @if (!readOnly) {
            <button type="button" (click)="togglePicker()" [attr.aria-label]="'Andere Quelle wählen'"
                    [title]="'Andere Quelle wählen'"
                    class="text-gray-400 hover:text-gray-700 transition-colors shrink-0">
              <fa-icon [icon]="faPen" class="text-xs"></fa-icon>
            </button>
            <button type="button" (click)="clear()" [attr.aria-label]="'Bindung entfernen'"
                    [title]="'Bindung entfernen'"
                    class="text-gray-400 hover:text-red transition-colors shrink-0">
              <fa-icon [icon]="faXmark"></fa-icon>
            </button>
          }
        </div>
        @if (boundIncompatibility) {
          <!-- Bestandsbindung an eine Quelle mit unpassenden Werten (vor der Werte-Prüfung im Picker
               möglich): sichtbar machen statt erst im Lauf scheitern. -->
          <span class="text-xs text-red-700 leading-tight">{{ boundIncompatibility }} – bitte eine andere Quelle wählen.</span>
        } @else if (legend.length) {
          <!-- Was die Quelle liefern kann. Bei einem Status-Feld mit denselben Icons/Farben wie die
               Status-Auswahl, damit erkennbar ist, welcher Wert zu welcher Stufe wird. -->
          <div class="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500 leading-tight">
            <span>Mögliche Werte:</span>
            @for (entry of legend; track entry.value) {
              <span class="inline-flex items-center gap-1 whitespace-nowrap">
                @if (entry.icon) {
                  <fa-icon [icon]="entry.icon" [ngClass]="entry.colorClass"></fa-icon>
                }
                {{ entry.label }}
              </span>
            }
          </div>
        }
      } @else if (unbound) {
        <!-- Ohne Wert, mit x-unbound: kein Freitextfeld, sondern was ohne Verknüpfung gilt
             (IFC-Selektion: „Verbunden – Datei des Prüflaufs"), daneben der Weg zur Quelle. -->
        <div class="flex items-start gap-2">
          <div class="flex items-center gap-2 flex-1 min-w-0 min-h-[38px] px-3 py-1.5 rounded-lg border
                      bg-gray-50 border-gray-300">
            @if (unbound.satisfied) {
              <fa-icon [icon]="faCircleCheck" class="text-green shrink-0"></fa-icon>
              <span class="text-sm text-gray-900 truncate">Verbunden – {{ unbound.label }}</span>
            } @else {
              <fa-icon [icon]="faCircle" class="text-gray-300 shrink-0"></fa-icon>
              <span class="text-sm text-gray-500 truncate">{{ unbound.label }}</span>
            }
          </div>
          @if (!readOnly) {
            <button type="button" (click)="togglePicker()"
                    [attr.aria-label]="'Wert aus vorherigem Schritt'"
                    [title]="'Wert aus einem vorherigen Schritt verwenden'"
                    class="flex items-center justify-center h-[38px] w-[38px] shrink-0 rounded-lg border
                           border-gray-300 bg-gray-50 text-gray-500
                           hover:border-gray-400 hover:text-gray-700 transition-colors">
              <fa-icon [icon]="faBolt" class="text-sm"></fa-icon>
            </button>
          }
        </div>
      } @else {
        <!-- Festwert (oder leer): das schema-getriebene Widget, daneben der Weg zum Kontext-Wert. -->
        <div class="flex items-start gap-2">
          <div class="flex-1 min-w-0">
            <sf-leaf
              [schema]="schema"
              [widgetOverride]="literalWidget"
              [disabled]="readOnly || pickerOpen"
              [value]="value"
              (valueChange)="literalChange.emit($event)">
            </sf-leaf>
          </div>
          @if (!readOnly) {
            <button type="button" (click)="togglePicker()"
                    [attr.aria-label]="'Wert aus vorherigem Schritt'"
                    [title]="'Wert aus einem vorherigen Schritt verwenden'"
                    class="flex items-center justify-center h-[38px] w-[38px] shrink-0 rounded-lg border
                           border-gray-300 bg-gray-50 text-gray-500
                           hover:border-gray-400 hover:text-gray-700 transition-colors">
              <fa-icon [icon]="faBolt" class="text-sm"></fa-icon>
            </button>
          }
        </div>
        @if (hint) {
          <span class="text-xs text-gray-400 leading-tight">{{ hint }}</span>
        }
      }

      @if (pickerOpen) {
        <!-- Auswahl der Vorgängerwerte, nach Quellschritt gruppiert. -->
        <div class="absolute z-30 top-full left-0 right-0 mt-1 flex flex-col gap-2 p-3
                    bg-white border border-gray-300 rounded-lg shadow-lg">
          @if (!candidates.length) {
            <span class="text-xs text-gray-400 py-1">
              Keine Werte verfügbar — verbinde diesen Schritt zuerst im Ablauf.
            </span>
          } @else {
            <input
              [(ngModel)]="query"
              class="w-full h-[34px] px-3 text-sm text-gray-900 bg-gray-50 border border-gray-300 rounded-lg
                     focus:outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-200 transition-colors"
              placeholder="Wert suchen…"
              type="text"/>
            @if (!groups.length) {
              <span class="text-xs text-gray-400 py-1">Kein Treffer</span>
            }
            <div class="flex flex-col gap-2 max-h-[220px] overflow-y-auto">
              @for (group of groups; track group.nodeName) {
                <div class="flex flex-col gap-1">
                  <span class="text-xs font-medium text-gray-500 uppercase">{{ group.nodeName }}</span>
                  @for (variable of group.variables; track variable.ref) {
                    <button type="button" (click)="choose(variable)"
                            [disabled]="!isCandidateCompatible(variable)"
                            [title]="incompatibilityReason(variable) ?? ''"
                            class="flex flex-wrap items-center gap-x-2 gap-y-1 px-2 py-1.5 rounded-lg text-left
                                   border border-gray-200 hover:border-gray-400 hover:bg-gray-50 transition-colors
                                   disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-gray-200
                                   disabled:hover:bg-transparent">
                      <span class="text-sm text-gray-900">
                        {{ variable.ambiguous ? variable.qualifiedName : variable.name }}
                      </span>
                      @if (!isCandidateCompatible(variable)) {
                        <span class="text-[11px] text-gray-400 italic">Passt nicht zu diesem Feld</span>
                      }
                      @if (variable.options?.length) {
                        @for (option of variable.options; track option) {
                          <span class="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100
                                       text-[11px] font-medium whitespace-nowrap">{{ option }}</span>
                        }
                      }
                    </button>
                  }
                </div>
              }
            </div>
          }
          <button type="button" (click)="pickerOpen = false"
                  class="self-end text-xs text-gray-400 hover:text-gray-700 transition-colors">
            Schließen
          </button>
        </div>
      }
    </div>
  `,
})
export class SfValueBindingComponent {
  /**
   * Der gespeicherte Wert: entweder ein Festwert oder der rohe `${…}`-String einer Referenz.
   * Beide liegen an derselben Stelle — genau das ist die Transportform, die die Engine erwartet.
   */
  @Input() value: any;
  /** Schema des Werts — treibt das Festwert-Widget. */
  @Input() schema: JsonSchema = {};
  /** Hilfszeile unter dem Festwert-Feld. */
  @Input() hint = '';
  /** Wählbare Werte — nur Ausgaben von Schritten, die im Ablauf VOR diesem liegen. */
  @Input() candidates: BindingCandidate[] = [];
  /**
   * Anzeige der gebundenen Quelle, wenn der Aufrufer sie besser kennt als die Kandidatenliste.
   *
   * Für Eingangs-Ports löst das Panel die Referenz direkt gegen das Manifest auf — eine
   * Bestandsreferenz kann auf einen Knoten zeigen, der kein Fluss-Vorgänger (mehr) ist und
   * deshalb nicht in `candidates` steht. Ohne diesen Vorrang verschwände so eine Bindung
   * stillschweigend aus der Oberfläche, während sie zur Laufzeit weiterwirkt.
   */
  @Input() source: BindingSource | null = null;
  @Input() readOnly = false;

  @Output() literalChange = new EventEmitter<any>();
  @Output() referenceChange = new EventEmitter<string>();
  @Output() cleared = new EventEmitter<void>();
  pickerOpen = false;
  query = '';
  protected readonly faBolt = faBolt;
  protected readonly faCircle = faCircle;
  protected readonly faCircleCheck = faCircleCheck;
  protected readonly faPen = faPen;
  protected readonly faXmark = faXmark;
  private readonly memo = createMemo();

  /**
   * True, wenn der Wert eine `${…}`-Referenz ist statt eines Festwerts.
   *
   * Bewusst dieselbe grobe Prüfung wie `isVariableReference` in `workflow.models`: alles in
   * `${…}` ist eine Referenz. Eine engere Form würde eine kaputte Referenz als Festwert
   * anzeigen und beim nächsten Speichern stillschweigend zu Text machen.
   */
  /**
   * `x-unbound` greift, solange kein Wert gesetzt ist: das Feld wird nur verknüpft, nie frei
   * eingetragen, und zeigt an, was ohne Verknüpfung gilt.
   */
  get unbound(): { label: string; satisfied: boolean } | null {
    const spec = this.schema?.['x-unbound'];
    if (!spec?.label) return null;
    const empty = this.value === undefined || this.value === null
      || (typeof this.value === 'string' && this.value.trim() === '');
    return empty ? {label: spec.label, satisfied: spec.satisfied === true} : null;
  }

  get isReference(): boolean {
    const raw = typeof this.value === 'string' ? this.value.trim() : '';
    return raw.startsWith('${') && raw.endsWith('}');
  }

  /**
   * Quelle für die Anzeige: der explizit gesetzte Vorrang, sonst der passende Kandidat.
   *
   * Der Rückfall auf die Kandidatenliste ist das, was das Widget in Einstellungen tragfähig
   * macht — dort gibt es kein Panel, das die Referenz auflöst. Findet sich der Ref nicht,
   * ist die Bindung tot und wird auch so gezeigt statt still zu verschwinden.
   */
  get resolvedSource(): BindingSource | null {
    if (this.source) return this.source;
    const ref = typeof this.value === 'string' ? this.value.trim() : '';
    const match = this.candidates.find(c => c.ref === ref);
    if (!match) return {nodeName: '', name: ref, dangling: true};
    return {nodeName: match.nodeName, name: match.name, options: match.options, dangling: false};
  }

  /**
   * Legende unter einer gebundenen Quelle: welche Werte sie liefern kann.
   *
   * Bei einem Status-Feld (`x-literal-widget`/`x-widget: status`) mit Icon und Farbe aus
   * {@link classifyStatusValue} und dem Anzeigenamen des Ziel-Felds (`x-enumLabels`), sonst nur
   * die Werte. Leer bei toter Bindung oder einer Quelle ohne Auswahl.
   */
  get legend(): BindingLegendEntry[] {
    const source = this.resolvedSource;
    const options = source && !source.dangling ? source.options : undefined;
    return this.memo('legend', [options, this.schema], () => {
      if (!options?.length) return [];
      const isStatus = this.schema?.['x-literal-widget'] === 'status' || this.schema?.['x-widget'] === 'status';
      return options.map(option => isStatus
        ? {value: option, label: enumLabel(this.schema, option), ...classifyStatusValue(option)}
        : {value: option, label: option});
    });
  }

  /** Unverträglichkeit der aktuell gebundenen Quelle (siehe {@link incompatibilityReason}), oder null. */
  get boundIncompatibility(): string | null {
    const source = this.resolvedSource;
    if (!this.isReference || !source || source.dangling) return null;
    return this.incompatibilityReason({ref: '', name: source.name, qualifiedName: source.name,
      nodeName: source.nodeName, options: source.options});
  }

  /**
   * Widget des Festwert-Felds.
   *
   * Reihenfolge:
   * 1. `x-literal-widget` — der Ausweg für Felder, die als Festwert ein besonderes Eingabefeld
   *    brauchen: Der Status der Nutzerantwort zeigt seine Stufen mit Icon (`status`), wäre über den
   *    Schema-Aufbau aber nur ein gewöhnliches Dropdown. Ohne diesen Haken kostet „bindbar machen"
   *    jedes Mal das spezialisierte Eingabefeld.
   * 2. ein explizites `x-widget` (z.B. `'files'` für einen Datei-Eingang) — außer es ist selbst
   *    `'value-binding'` (dort steht dann diese Bindung).
   * 3. sonst aus dem Schema-Aufbau.
   */
  get literalWidget(): WidgetName {
    const literal = this.schema?.['x-literal-widget'];
    if (literal) return literal as WidgetName;
    const explicit = this.schema?.['x-widget'];
    if (explicit && explicit !== 'value-binding') return explicit as WidgetName;
    return structuralWidget(this.schema);
  }

  /** Kandidaten nach Quellschritt gruppiert, in Ablaufreihenfolge (die Liste kommt sortiert). */
  get groups(): { nodeName: string; variables: BindingCandidate[] }[] {
    const needle = this.query.trim().toLowerCase();
    const grouped: { nodeName: string; variables: BindingCandidate[] }[] = [];
    for (const variable of this.candidates) {
      if (needle && !`${variable.nodeName} ${variable.name}`.toLowerCase().includes(needle)) continue;
      const last = grouped[grouped.length - 1];
      if (last && last.nodeName === variable.nodeName) last.variables.push(variable);
      else grouped.push({nodeName: variable.nodeName, variables: [variable]});
    }
    return grouped;
  }

  togglePicker(): void {
    if (this.readOnly) return;
    this.pickerOpen = !this.pickerOpen;
    if (this.pickerOpen) this.query = '';
  }

  /**
   * Ob ein Kandidat zum Ziel-Schema dieses Felds passt — die wiederhergestellte
   * Typ-Kompatibilitätsprüfung aus dem früheren Datenport-System (string/number/boolean/object/
   * array, `integer` zählt als `number`). Anders als damals wird eine inkompatible Quelle nicht
   * aus der Liste entfernt, sondern nur deaktiviert gezeigt: die vollständige Liste bleibt
   * durchsuchbar, aber nicht anwählbar, mit einem verständlichen Hinweis statt technischer
   * Typnamen (siehe Template).
   */
  isCandidateCompatible(candidate: BindingCandidate): boolean {
    return this.incompatibilityReason(candidate) === null;
  }

  /**
   * Warum ein Kandidat nicht passt, oder `null`.
   *
   * Neben dem Datentyp zählen die Werte: Erlaubt das Ziel nur bestimmte Werte (`enum`, z.B. der
   * Status der Nutzerantwort) und nennt die Quelle ihre möglichen Werte, müssen alle davon erlaubt
   * sein. Sonst ließ sich der Status an `Ergebnis` (SUCCESS/WARNING/ERROR) binden — der Lauf scheiterte
   * dann erst im Modul mit „Ungültiger Status". Quellen ohne Werteliste (freier Text) bleiben wählbar.
   */
  incompatibilityReason(candidate: BindingCandidate): string | null {
    if (!isTypeCompatible(this.schema?.type, candidate.type)) {
      return 'Passt nicht zu diesem Feld (unterschiedlicher Datentyp)';
    }
    const allowed = this.schema?.enum;
    if (Array.isArray(allowed) && allowed.length && candidate.options?.length) {
      const allowedValues = new Set(allowed.map(value => String(value)));
      const foreign = candidate.options.filter(option => !allowedValues.has(option));
      if (foreign.length === 1) return `Passt nicht zu diesem Feld: ${foreign[0]} ist hier kein gültiger Wert`;
      if (foreign.length > 1) return `Passt nicht zu diesem Feld: ${foreign.join(', ')} sind hier keine gültigen Werte`;
    }
    return null;
  }

  choose(variable: BindingCandidate): void {
    this.pickerOpen = false;
    this.referenceChange.emit(variable.ref);
  }

  clear(): void {
    this.pickerOpen = false;
    this.cleared.emit();
  }
}
